import { graphqlQuery } from '@/lib/api/graphql';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useModeAwareQuery } from './useModeAwareQuery';
import { RECEIPT_TOTALS_QUERY, type ReceiptTotalsGraphQLResponse } from './useReceiptsGraphQL';

export interface UnallocatedPaymentsSummaryApi {
  unallocatedCount: number;
  totalOutstandingAmount: number;
}

export interface UnallocatedPaymentsSummaryParams {
  creditControllerId?: string;
  allocationStatus?: 'unallocated';
  outstandingNotZero?: boolean;
}

export function buildUnallocatedPaymentsFilter(params: UnallocatedPaymentsSummaryParams): Record<string, unknown> {
  const filter: Record<string, unknown> = {
    allocationStatusCode: params.allocationStatus ? { eq: params.allocationStatus } : undefined,
    outstandingAmount: params.outstandingNotZero ? { ne: 0 } : undefined,
    creditControllerId: params.creditControllerId ? { eq: Number(params.creditControllerId) } : undefined,
  };
  return Object.fromEntries(Object.entries(filter).filter(([, value]) => value !== undefined));
}

/**
 * Hook to fetch unallocated payments summary.
 * In live mode, calls the GraphQL receiptTotals query.
 * In demo mode, returns data derived from the global data store.
 * @param params — receipt allocation filters matching the destination screen.
 * @param enabled — prevents an unscoped request while controller validation is pending.
 */
export function useUnallocatedPaymentsSummary(params: UnallocatedPaymentsSummaryParams, enabled = true) {
  const apiConfig = useApiConfig();

  const store = getGlobalDataStore();
  const unallocatedReceipts = store.getAllReceipts().filter((x) => {
    const outstandingAmount = x.homeUnappliedAmount ?? x.unappliedAmount;
    return (
      (!params.creditControllerId || x.creditControllerId === params.creditControllerId) &&
      (!params.allocationStatus || x.allocationStatus === params.allocationStatus) &&
      (!params.outstandingNotZero || outstandingAmount !== 0)
    );
  });
  const demoData: UnallocatedPaymentsSummaryApi = {
    unallocatedCount: unallocatedReceipts.length,
    totalOutstandingAmount: unallocatedReceipts.reduce(
      (sum, x) => sum + (x.homeUnappliedAmount ?? x.unappliedAmount),
      0,
    ),
  };

  return useModeAwareQuery<UnallocatedPaymentsSummaryApi, Error>(
    ['unallocated-payments-summary', params],
    async ({ signal }) => {
      const response = await graphqlQuery<ReceiptTotalsGraphQLResponse>(
        apiConfig,
        RECEIPT_TOTALS_QUERY,
        { filter: buildUnallocatedPaymentsFilter(params) },
        signal,
      );

      const totals = response.receipts.receiptTotals;

      return {
        unallocatedCount: totals.unallocatedCount,
        totalOutstandingAmount: totals.unallocatedAmount,
      };
    },
    demoData,
    { enabled, staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false },
  );
}
